<template>
  <div class="other-ligue-club">
    <div class="form-group">
      <label>N° de licence *</label>
      <input :value="licenseNumber" class="form-input" inputmode="numeric" placeholder="Ex. 34018003"
        autocomplete="off" @input="onNumberInput(($event.target as HTMLInputElement).value)" />
    </div>

    <p v-if="mode === 'loading'" class="olc-hint">Recherche du club…</p>

    <!-- Club déduit du numéro, à faire valider par le joueur. -->
    <div v-else-if="mode === 'found' && found" class="olc-card">
      <div>
        <strong>{{ found.name }}</strong>
        <span class="olc-meta">{{ structureLabel(found.comite_name, found.comite, found.ligue_name, found.ligue) }}</span>
      </div>
      <div class="olc-actions">
        <button type="button" class="olc-btn olc-btn-primary" @click="confirmFound">C'est mon club</button>
        <button type="button" class="olc-btn" @click="openManual">Ce n'est pas mon club</button>
      </div>
    </div>

    <div v-else-if="mode === 'confirmed'" class="olc-card olc-confirmed">
      <div>
        ✓ <strong>{{ clubName }}</strong>
        <span v-if="!clubId" class="olc-meta">club saisi</span>
      </div>
      <button type="button" class="olc-btn" @click="openManual">Modifier</button>
    </div>

    <!-- Choix manuel : ligue -> comité -> club, ou saisie libre si le club n'y est pas. -->
    <div v-else-if="mode === 'manual'" class="olc-manual">
      <p v-if="manualReason" class="olc-hint">{{ manualReason }}</p>
      <div class="olc-selects">
        <select v-model="selLigue" class="form-input" @change="selComite = ''; selClub = ''">
          <option value="">Ligue…</option>
          <option v-for="l in ligues" :value="l.code" :key="l.code">{{ l.name }}</option>
        </select>
        <select v-model="selComite" class="form-input" :disabled="!selLigue" @change="selClub = ''">
          <option value="">Comité…</option>
          <option v-for="c in comitesOfLigue" :value="c.code" :key="c.code">{{ c.name }}</option>
        </select>
        <select v-model="selClub" class="form-input" :disabled="!selComite">
          <option value="">Club…</option>
          <option v-for="c in clubsOfComite" :value="String(c.id)" :key="c.id">{{ c.name }}</option>
          <option :value="OTHER">Mon club n'est pas dans la liste</option>
        </select>
      </div>
      <input v-if="selClub === OTHER" v-model="typedName" class="form-input" maxlength="100"
        placeholder="Nom de votre club" />
      <button type="button" class="olc-btn olc-btn-primary" :disabled="!manualReady" @click="confirmManual">
        Valider ce club
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import backendApi from '@/axios/backendApi'

interface MinimalClub { id: number; name: string; comite: string | null; ligue: string | null }
interface Named { code: string; name: string; ligue_code?: string | null }

// Clubs, ligues et comités chargés une seule fois pour toute la page, quel que soit le
// nombre de joueurs qui ouvrent le choix manuel.
let directory: Promise<[MinimalClub[], Named[], Named[]]> | null = null
const loadClubDirectory = () => {
  directory ??= Promise.all([
    backendApi.get('/clubs/all-minimal').then(r => r.data),
    backendApi.get('/clubs/ligues').then(r => r.data),
    backendApi.get('/clubs/comites').then(r => r.data),
  ])
  directory.catch(() => { directory = null })
  return directory
}
</script>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'

// Licencié hors du périmètre de la liste (autre comité/ligue) : numéro de licence
// obligatoire, club déduit du numéro (cf.
// Dartshub inscriptions/license_link.py) à valider, sinon choisi ligue -> comité -> club,
// sinon saisi. Le club n'est transmis (clubId/clubName) qu'une fois validé.
const props = defineProps<{ licenseNumber: string; clubId: number | null; clubName: string }>()
const emit = defineEmits<{
  'update:licenseNumber': [value: string]
  'update:clubId': [value: number | null]
  'update:clubName': [value: string]
}>()

interface FoundClub { id: number; name: string; comite: string | null; comite_name: string | null; ligue: string | null; ligue_name: string | null }

const OTHER = '__other__'
const mode = ref<'idle' | 'loading' | 'found' | 'confirmed' | 'manual'>(props.clubName ? 'confirmed' : 'idle')
const found = ref<FoundClub | null>(null)
const manualReason = ref('')
const selLigue = ref('')
const selComite = ref('')
const selClub = ref('')
const typedName = ref('')

const structureLabel = (comiteName: string | null, comite: string | null, ligueName: string | null, ligue: string | null) =>
  [comiteName ? `${comiteName} (${comite})` : comite, ligueName ? `Ligue ${ligueName}` : ligue].filter(Boolean).join(' · ')

const setClub = (id: number | null, name: string) => {
  emit('update:clubId', id)
  emit('update:clubName', name)
}

let lookupTimer: ReturnType<typeof setTimeout> | undefined
let lookupSeq = 0

const onNumberInput = (raw: string) => {
  const digits = raw.replace(/\D/g, '').slice(0, 9)
  emit('update:licenseNumber', digits)
  setClub(null, '')
  found.value = null
  clearTimeout(lookupTimer)
  if (digits.length < 6) {
    mode.value = 'idle'
    return
  }
  mode.value = 'loading'
  lookupTimer = setTimeout(() => lookup(digits), 400)
}

const lookup = async (digits: string) => {
  const seq = ++lookupSeq
  try {
    const { data } = await backendApi.get(`/inscriptions/other-ligue-license/${digits}`)
    if (seq !== lookupSeq) return
    if (data.club) {
      found.value = data.club
      mode.value = 'found'
    } else {
      openManual('Aucun club connu pour ce numéro : sélectionnez le vôtre.')
    }
  } catch {
    if (seq === lookupSeq) openManual('Recherche impossible : sélectionnez votre club.')
  }
}

const confirmFound = () => {
  if (!found.value) return
  setClub(found.value.id, found.value.name)
  mode.value = 'confirmed'
}

// ── Choix manuel ──────────────────────────────────────────────────────────────
const clubs = ref<MinimalClub[]>([])
const ligues = ref<Named[]>([])
const comites = ref<Named[]>([])
const loadDirectory = () => loadClubDirectory().then(([c, l, co]) => {
  clubs.value = c
  ligues.value = l
  comites.value = co
}).catch(() => { /* listes vides : la saisie libre reste possible */ })

const comitesOfLigue = computed(() => comites.value.filter(c => c.ligue_code === selLigue.value))
const clubsOfComite = computed(() => clubs.value.filter(c => c.comite === selComite.value))
const manualReady = computed(() => selClub.value === OTHER ? !!typedName.value.trim() : !!selClub.value)

const openManual = (reason?: string | Event) => {
  manualReason.value = typeof reason === 'string' ? reason : ''
  setClub(null, '')
  // Prérempli avec le club proposé (ou déjà validé) pour ne corriger que ce qui est faux.
  const base = found.value
  selLigue.value = base?.ligue ?? ''
  selComite.value = base?.comite ?? ''
  selClub.value = ''
  typedName.value = ''
  mode.value = 'manual'
  loadDirectory()
}

const confirmManual = () => {
  if (selClub.value === OTHER) {
    setClub(null, typedName.value.trim().replace(/\s+/g, ' '))
  } else {
    const club = clubs.value.find(c => String(c.id) === selClub.value)
    if (!club) return
    setClub(club.id, club.name)
  }
  mode.value = 'confirmed'
}

onMounted(() => {
  // Panier restauré (retour de HelloAsso...) : numéro saisi mais club pas encore validé.
  if (mode.value === 'idle' && props.licenseNumber.length >= 6) {
    mode.value = 'loading'
    lookup(props.licenseNumber)
  }
})
</script>

<style scoped>
.other-ligue-club {
  margin-top: 0.5rem;
  padding: 0.7rem;
  border: 1px dashed var(--color-light-shadow, #ddd);
  border-radius: var(--radius, 8px);
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  display: block;
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 0.3rem;
}

.olc-hint {
  margin: 0;
  font-size: 0.8rem;
  color: var(--color-fg-darker, #666);
}

.olc-card {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.olc-meta {
  display: block;
  font-size: 0.8rem;
  color: var(--color-fg-darker, #666);
}

.olc-confirmed strong {
  color: var(--color-success, #2e7d32);
}

.olc-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.olc-manual {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.olc-selects {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
}

@media (max-width: 600px) {
  .olc-selects {
    grid-template-columns: 1fr;
  }
}

.olc-btn {
  padding: 0.45rem 0.8rem;
  background: transparent;
  border: 1px solid var(--color-light-shadow, #ddd);
  color: var(--color-fg, #333);
  border-radius: var(--radius, 8px);
  cursor: pointer;
  font-size: 0.85rem;
  align-self: flex-start;
}

.olc-btn-primary {
  background: var(--color-accent);
  border-color: var(--color-accent);
  color: #fff;
}

.olc-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
